import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { LoadingSpinner } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  fetchYowanBlogAuthorPostList,
  formatYowanAdminDateTime,
  YOWAN_CONTENT_ADMIN_PAGE_SIZE,
} from '../app/yowanContentClient';
import {
  AdminListPagination,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import {
  BlogAuthorGuardNotice,
  BlogAuthorShell,
  useBlogAuthorGuard,
} from './BlogAuthorDashboardPage';
const { yowanPageContent } = require('./pageContent');

const tableStyle = { minWidth: '64rem' };
const titleColumnStyle = { minWidth: '20rem' };
const statusColumnStyle = { width: '9rem', minWidth: '9rem' };
const dateColumnStyle = { width: '12rem', minWidth: '12rem' };
const actionColumnStyle = { width: '9rem', minWidth: '9rem' };

const BlogAuthorPostsPage = ({ guardOptions }) => {
  const content = yowanPageContent['/blog-author/posts'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const guard = useBlogAuthorGuard(guardOptions);
  const [currentPage, setCurrentPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [apiCount, setApiCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (guard.status !== 'ready') {
      return undefined;
    }

    let isMounted = true;
    setIsLoading(true);
    setLoadError('');

    fetchYowanBlogAuthorPostList({
      page: currentPage,
      pageSize: YOWAN_CONTENT_ADMIN_PAGE_SIZE,
    })
      .then((payload) => {
        if (!isMounted) {
          return;
        }

        setRows(payload.results);
        setApiCount(payload.count);
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }

        setRows([]);
        setApiCount(0);
        setLoadError(content?.loadErrorMessage || '投稿を読み込めませんでした。');
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [content?.loadErrorMessage, currentPage, guard.status]);

  if (guard.status !== 'ready') {
    return <BlogAuthorGuardNotice notice={guard.notice} />;
  }

  if (!content) {
    return null;
  }

  const totalPages = Math.max(1, Math.ceil(apiCount / YOWAN_CONTENT_ADMIN_PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);

  return (
    <BlogAuthorShell activeNavHref="/blog-author/posts" routeKey="/blog-author/posts">
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link
            key="primary-action"
            className="c-form__submit"
            to={appendYowanPreviewQueryIfNeeded('/blog-author/posts/new', preservedDomainSearch)}
          >
            新規投稿を作成
          </Link>,
        ]}
      />

      <div className="yowan-admin-resource-table-wrap c-surface-content">
        <table className="yowan-admin-resource-table" style={tableStyle}>
          <thead>
            <tr>
              <th scope="col" style={titleColumnStyle}>タイトル</th>
              <th scope="col" style={statusColumnStyle}>公開</th>
              <th scope="col" style={dateColumnStyle}>公開開始日時</th>
              <th scope="col" style={dateColumnStyle}>更新日</th>
              <th scope="col" style={actionColumnStyle}>操作</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td style={titleColumnStyle}>
                  <p className="yowan-admin-resource-table__title">{row.title || '-'}</p>
                  <p className="yowan-admin-resource-table__subline">{row.slug || '-'}</p>
                </td>
                <td style={statusColumnStyle}>
                  <span className={`yowan-admin-resource-table__status yowan-admin-resource-table__status--${row.isPublished ? 'published' : 'draft'}`}>
                    {row.isPublished ? '公開中' : '下書き'}
                  </span>
                </td>
                <td style={dateColumnStyle}>{formatYowanAdminDateTime(row.publishedAt) || '-'}</td>
                <td style={dateColumnStyle}>{formatYowanAdminDateTime(row.updatedAt) || '-'}</td>
                <td style={actionColumnStyle}>
                  <div className="yowan-admin-resource-table__actions">
                    <Link to={appendYowanPreviewQueryIfNeeded(row.editHref, preservedDomainSearch)}>編集</Link>
                    {row.publicHref ? (
                      <Link to={appendYowanPreviewQueryIfNeeded(row.publicHref, preservedDomainSearch)}>公開面</Link>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
            {!isLoading && rows.length === 0 ? (
              <tr>
                <td colSpan={5}>{loadError || '表示できる投稿がありません。'}</td>
              </tr>
            ) : null}
            {isLoading ? (
              <tr>
                <td colSpan={5}>
                  <LoadingSpinner size="sm" />
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <AdminListPagination
        currentPage={safeCurrentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </BlogAuthorShell>
  );
};

export default BlogAuthorPostsPage;
